import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Accordion, AccordionDetails, AccordionSummary, Divider, ExpandMoreIcon } from '@breathelife/mui';
import { AutoCompleteMultiple, AutocompleteOption, Box } from '@breathelife/ui-components';
import {
  ApplicationMode,
  ApplicationModes,
  InsuranceModule,
  PdfDocumentType,
  PdfDocumentTypes,
  PlatformType,
  PlatformTypes,
  QuestionnaireBlueprintCopyableOption,
  QuestionnaireBlueprintCopyableOptions,
  QuestionnaireBlueprintRenderOn,
  QuestionnaireBlueprintRenderOns,
} from '@zinnia/form-engine-sdk';

import { useCarrierContext } from '../../../../../Hooks';
import styled from '../../../../../Styles/themed-styled-components';
import { BlueprintContexts } from '../Summaries/BlueprintContexts';
import { EditorDropdown } from './EditorDropdown';
import { ModuleSelection } from './ModuleSelection';

type Props = {
  tag: 'section' | 'subsection' | 'question' | 'field';
  disabled: boolean;
  platform: { selected: PlatformType[]; onChange: (values: PlatformType[]) => void };
  renderOn: {
    selected: QuestionnaireBlueprintRenderOn[];
    onChange: (values: QuestionnaireBlueprintRenderOn[]) => void;
  };
  dataLabel: { selected?: string; options: AutocompleteOption[]; onChange: (updatedSelection: string | null) => void };
  pdfDocument?: { selected: PdfDocumentType[]; onChange: (values: PdfDocumentType[]) => void };
  insuranceModule?: { selected: InsuranceModule; onChange: (module: InsuranceModule) => void };
  applicationModes?: {
    selected: ApplicationMode[];
    onChange: (updatedType: ApplicationMode[]) => void;
  };
  copyable: {
    selected: QuestionnaireBlueprintCopyableOption;
    onChange: (updatedSelection: string | null) => void;
    disabled?: boolean;
  };
};

const BlueprintContextContainer = styled.div`
  display: flex;
  align-items: center;

  & > :last-child {
    margin-left: 4px;
  }
`;

export function AdvancedBlueprintOptions({
  disabled,
  tag,
  platform,
  renderOn,
  pdfDocument,
  dataLabel,
  insuranceModule,
  applicationModes,
  copyable,
}: Props): ReactElement {
  const { t } = useTranslation();
  const { features } = useCarrierContext();

  const dataLabelEntry = useMemo(() => {
    if (!dataLabel?.options.length) {
      return null;
    }

    const { options, selected, onChange } = dataLabel;
    return (
      <Box pt={2}>
        <EditorDropdown
          label={t('admin.questionnaireManagement.input.dataLabel')}
          defaultOption={options[0]}
          disabled={disabled}
          selectedOptionValue={selected}
          options={options}
          onChange={onChange}
        />
      </Box>
    );
  }, [dataLabel, disabled, t]);

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <BlueprintContextContainer>
          {t(`admin.questionnaireManagement.advancedOptions.${tag}`)}

          <BlueprintContexts
            platforms={platform.selected}
            renderOn={renderOn.selected}
            insuranceModules={insuranceModule && insuranceModule.selected ? [insuranceModule.selected] : undefined}
          />
        </BlueprintContextContainer>
      </AccordionSummary>
      <Divider />
      <AccordionDetails>
        <Box display='flex' flexDirection='column' width='100%'>
          <AutoCompleteMultiple
            id='platform-type-selection'
            label={t('admin.questionnaireManagement.platforms.title')}
            disabled={disabled}
            optionInfo={{
              items: Object.values(PlatformTypes),
              toOption: (item) => ({ value: item, label: t(`admin.questionnaireManagement.platforms.${item}`) }),
              selectedItems: platform.selected,
              onChange: platform.onChange,
            }}
          />
          <Box pt={2}>
            <AutoCompleteMultiple
              id='render-on-selection'
              label={t('admin.questionnaireManagement.rendering.title')}
              disabled={disabled}
              optionInfo={{
                items: Object.values(QuestionnaireBlueprintRenderOns),
                toOption: (item) => ({ value: item, label: t(`admin.questionnaireManagement.rendering.${item}`) }),
                selectedItems: renderOn.selected,
                onChange: renderOn.onChange,
              }}
            />
          </Box>
          {applicationModes && (
            <Box pt={2}>
              <AutoCompleteMultiple
                id='questionnaire-application-mode-selection'
                label={t('admin.questionnaireManagement.applicationMode.title')}
                disabled={disabled}
                optionInfo={{
                  selectedItems: applicationModes.selected,
                  items: Object.values(ApplicationModes),
                  toOption: (text: ApplicationMode) => ({
                    label: t(`admin.questionnaireManagement.applicationMode.${text}`),
                    value: text,
                  }),
                  onChange: applicationModes.onChange,
                }}
              />
            </Box>
          )}
          {pdfDocument && (
            <Box pt={2}>
              <AutoCompleteMultiple
                id='pdf-documents-selection'
                label={t('admin.questionnaireManagement.pdfDocuments.title')}
                disabled={disabled}
                optionInfo={{
                  items: Object.values(PdfDocumentTypes),
                  toOption: (item) => ({ value: item, label: t(`admin.questionnaireManagement.pdfDocuments.${item}`) }),
                  selectedItems: pdfDocument.selected,
                  onChange: pdfDocument.onChange,
                }}
              />
            </Box>
          )}
          {insuranceModule && (
            <Box pt={2}>
              <ModuleSelection
                selectedModule={insuranceModule.selected}
                onChange={insuranceModule.onChange}
                disabled={disabled}
              />
            </Box>
          )}
          {dataLabelEntry}
          {features.applicationCopy?.enabled && (
            <Box pt={2}>
              <EditorDropdown
                label={t('admin.questionnaireManagement.copyable.title')}
                defaultOption={{
                  label: t(`admin.questionnaireManagement.copyable.${QuestionnaireBlueprintCopyableOptions.none}`),
                  value: QuestionnaireBlueprintCopyableOptions.none,
                }}
                disabled={disabled || copyable.disabled}
                selectedOptionValue={copyable.selected}
                options={Object.values(QuestionnaireBlueprintCopyableOptions).map((option) => ({
                  label: t(`admin.questionnaireManagement.copyable.${option}`),
                  value: option,
                }))}
                onChange={copyable.onChange}
              />
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
